import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, Route, Wallet, Warehouse, Check, ArrowRight, Landmark } from "lucide-react";
import { useDemoModal } from "../context/DemoModalContext";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";
const ORANGE = "#FF5722";
const SLIDE_MS = 3000;

const ROLES = [
  {
    id: "driver",
    label: "I'm a Driver",
    tag: "Driver",
    icon: Truck,
    pains: [
      "No more circling lots at 2am hunting for parking",
      "A private pod, held and ready the moment you arrive",
      "Hours of service tracked automatically, no paperwork",
      "Secure, gated rest so you sleep easy",
      "One-tap booking along the route you're already driving",
    ],
    outcome: "Arrive rested, on time, every single night.",
    chips: ["0 wasted laps", "100% verified stops", "8h secure rest"],
    screens: [
      { src: null },
      { src: null },
      { src: null },
      { src: null },
      { src: null },
    ],
  },
  {
    id: "dispatcher",
    label: "I'm a Dispatcher",
    tag: "Dispatcher",
    icon: Route,
    pains: [
      "Your whole fleet's location & HOS in one live view",
      "Book a secure stop for any driver in a single click",
      "Fewer detours and far fewer compliance surprises",
      "No more phone-tag chasing down safe parking",
      "Predictable ETAs your customers actually trust",
    ],
    outcome: "Your whole fleet, moving with confidence.",
    chips: ["1 live view", "-32% detours", "Instant booking"],
    screens: [
      { src: null },
      { src: null },
      { src: null },
      { src: null },
      { src: null },
    ],
  },
  {
    id: "owner",
    label: "I'm an Owner-Operator",
    tag: "Owner-Operator",
    icon: Wallet,
    pains: [
      "Transparent pricing up front, no surprises at the gate",
      "Routes planned around guaranteed, restful stops",
      "Automatic HOS and a digital logbook, always ready",
      "Protect your margins with fewer wasted miles",
      "Rest secure tonight so you drive fresh tomorrow",
    ],
    outcome: "Every mile earns its keep.",
    chips: ["No surprise fees", "Auto HOS", "Healthier margins"],
    screens: [
      { src: null },
      { src: null },
      { src: null },
      { src: null },
      { src: null },
    ],
  },
  {
    id: "depot",
    label: "I run a Depot",
    tag: "Depot",
    icon: Warehouse,
    pains: [
      "Real-time visibility of every bay and slot",
      "Faster, verified check-ins at the gate",
      "Less gate chaos and quicker truck turnarounds",
      "Optimize dock usage from end to end",
      "Squeeze more throughput out of every bay",
    ],
    outcome: "A yard that practically runs itself.",
    chips: ["Live bay view", "Faster check-in", "More throughput"],
    screens: [
      { src: null },
      { src: null },
      { src: null },
      { src: null },
      { src: null },
    ],
  },
  {
    id: "gov",
    label: "a Government Agency",
    tag: "Gov't Agency",
    icon: Landmark,
    pains: [
      "Verified, auditable rest-stop records on demand",
      "Real-time compliance and hours-of-service oversight",
      "Secure, monitored locations across the network",
      "Fewer roadside incidents from fatigued drivers",
      "Standardized reporting across every carrier",
    ],
    outcome: "Safer roads, backed by real data.",
    chips: ["Auditable logs", "Live oversight", "Standard reports"],
    screens: [
      { src: null },
      { src: null },
      { src: null },
      { src: null },
      { src: null },
    ],
  },
];

// Autoplay slideshow of dashboard screenshots for the active entity.
// Cycles through `screens` every SLIDE_MS with a crossfade. Drop real image
// URLs into each role's `screens[].src` and they render automatically.
const PLACEHOLDERS = [{}, {}, {}, {}, {}];

const Showcase = ({ screens, tag }) => {
  const [i, setI] = useState(0);

  useEffect(() => {
    setI(0);
    if (screens.length < 2) return;
    const id = setInterval(() => setI((p) => (p + 1) % screens.length), SLIDE_MS);
    return () => clearInterval(id);
  }, [screens]);

  const slide = screens[i] || screens[0];

  return (
    <div
      data-testid="hiw-visual"
      className="relative min-h-[420px] overflow-hidden border-t border-black/5 lg:min-h-full lg:border-l lg:border-t-0"
      style={{ background: "#EDEFF3" }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={`${tag}-${i}`}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          transition={{ duration: 0.6, ease: EASE }}
          className="absolute inset-0"
        >
          {slide.src ? (
            <img
              src={slide.src}
              alt={`FleetPods app for ${tag}`}
              className="h-full w-full object-cover"
              draggable="false"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{
                backgroundColor: "#E9EBEF",
                backgroundImage:
                  "repeating-linear-gradient(45deg,#dfe2e7 0px,#dfe2e7 2px,transparent 2px,transparent 12px)",
              }}
            >
              <span className="text-[13px] font-600 uppercase tracking-[0.14em] text-neutral-400">
                Img placeholder
              </span>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* progress dots */}
      {screens.length > 1 && (
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-1.5">
          {screens.map((_, idx) => (
            <span
              key={idx}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: idx === i ? 18 : 6,
                background: idx === i ? ORANGE : "rgba(10,35,74,0.25)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const HowItWorks = () => {
  const { openDemo } = useDemoModal();
  const [role, setRole] = useState("driver");
  const active = ROLES.find((r) => r.id === role) || ROLES[0];

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative overflow-hidden py-20 lg:py-28"
      style={{
        backgroundImage: "url(/wave-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >

      <div className="relative mx-auto w-full max-w-[1180px] px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-sm font-700 uppercase tracking-[0.18em] text-[#FF5722]">
            See how it works
          </p>
          <h2
            className="font-display text-3xl font-700 leading-[1.1] tracking-tight sm:text-5xl"
            style={{ color: NAVY }}
          >
            Tell us who you are.
            <span className="block text-[#FF5722]">We&apos;ll show you the ride.</span>
          </h2>
        </div>

        {/* white split panel */}
        <div className="grid gap-0 overflow-hidden rounded-[28px] border border-black/5 bg-white shadow-[0_30px_60px_-30px_rgba(10,35,74,0.35)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          {/* LEFT PANE — toggles + pain points */}
          <div className="p-6 sm:p-9 lg:p-10">
            {/* "I'm a..." prompt */}
            <p
              className="mb-3 text-[15px] font-600"
              style={{ color: NAVY }}
              data-testid="hiw-prompt"
            >
              I&apos;m a<span className="text-[#FF5722]">…</span>
            </p>

            {/* short horizontal segmented role toggle — one line, orange stroke */}
            <div
              data-testid="hiw-role-toggle"
              className="mb-8 flex w-full flex-nowrap gap-1 rounded-2xl bg-[#F1F3F7] p-1 ring-1 ring-[#FF5722]"
            >
              {ROLES.map((r) => {
                const on = r.id === role;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`hiw-role-${r.id}`}
                    onClick={() => setRole(r.id)}
                    className={`relative z-10 flex-1 whitespace-nowrap rounded-xl px-2.5 py-2 text-center text-[12px] font-600 transition-colors duration-300 ${
                      on ? "text-white" : "text-neutral-600 hover:text-neutral-900"
                    }`}
                  >
                    {on && (
                      <motion.span
                        layoutId="hiw-pill"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        className="absolute inset-0 -z-10 rounded-xl bg-[#FF5722] shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)]"
                      />
                    )}
                    <span className="relative">{r.tag}</span>
                  </button>
                );
              })}
            </div>

            {/* 5 pain-point bullets */}
            <AnimatePresence mode="wait">
              <motion.ul
                key={role}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0, transition: { duration: 0.15 } }}
                className="space-y-3.5"
              >
                {active.pains.map((p, i) => (
                  <motion.li
                    key={p}
                    variants={{
                      hidden: { opacity: 0, x: -14 },
                      show: (idx) => ({
                        opacity: 1,
                        x: 0,
                        transition: { delay: idx * 0.07, duration: 0.5, ease: EASE },
                      }),
                    }}
                    custom={i}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#FF5722]/10">
                      <Check className="h-3.5 w-3.5 text-[#FF5722]" strokeWidth={3} />
                    </span>
                    <span className="text-[15px] font-500 leading-relaxed text-neutral-700">
                      {p}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </AnimatePresence>

            {/* book a demo escape hatch */}
            <a
              href="#"
              data-testid="hiw-book-demo"
              onClick={(e) => { e.preventDefault(); openDemo(); }}
              className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-[#FF5722] px-6 py-3 text-[15px] font-600 text-white shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F4511E] active:scale-[0.98]"
            >
              Request a Demo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>

          {/* RIGHT PANE — grey placeholder slideshow (real images added later) */}
          <Showcase screens={PLACEHOLDERS} tag={active.tag} />
        </div>
      </div>
    </section>
  );
};
